/* eslint-disable jsx-a11y/alt-text */
import { useTranslation } from 'react-i18next';
import _ from 'lodash';
import dayjs from 'dayjs';
import i18next from 'i18next';

import { DatiVeicoloEntity, OptionalEntity } from '../../api';
import { useAiContext } from '../../context/AiContext';
import './QuickoPdfTemplate.scss';
import { getCheckedServices } from '../../utils/quicko';
import { useEffect, useMemo, useState } from 'react';
import usePdfStore from '../../store/PdfStore';
import { fixSpaces, generatePdf } from '../../utils/pdfUtils';
import { numberFormat } from 'src/utils';
import { resolveReminderNumber } from 'src/utils/reminderNumber';
import { shallow } from "zustand/shallow"
import { useQuickoHelper } from 'src/hooks/useQuickoHelper';
import { useQuotationRecorder } from 'src/quotations/useQuotationRecorder';

export type PDFData = {
	lista_offerte: {
		id: string;
		durata: string;
		km_totali: string;
		servizi: {
			treno_di_gomme: string;
			kasko: string;
			rca: string;
			auto_sostitutiva: string;
			i_f: string;
		};
		anticipo: string;
	}[];
	pdf: {
		data_validita: string;
		name: string;
		userName: string;
		userSurname: string;
		userMail: string;
		offerType: string;
		offerId: string;
		importo_azienda: string;
		importo_privato: string;
		dealerDetail: {
			logo: string;
		};
	};
	optionals: OptionalEntity[];
	dati_veicolo: DatiVeicoloEntity;
};

function QuickoPdfTemplate() {
	const maxlen = 500;
	const {
		quotReqState: { totalKm, rentalDuration, advancePayment },
		services,
		closeModals,
		openSignalingModal,
	} = useAiContext();
	const { t } = useTranslation();
	const checkedServices = getCheckedServices(services);
	const [data, setData] = useState<any>({});
	const [offer, setoffer] = useState<any>({});
	const [filename, setFilename] = useState<string | null>(null);
	const quickoHelper = useQuickoHelper();
	const quotationRecorder = useQuotationRecorder();

	const unsubscribe = usePdfStore.subscribe(
		(state) => state.quickoDetail,
		(quickoDetail, prevQuickoDetail) => {
			if (quickoDetail && quickoDetail !== prevQuickoDetail) {
				quickoDetail.data.row = quickoDetail.data.pdf.processedRow.original;
				downloadPdf({ ...quickoDetail });
			}
		},
		{ equalityFn: shallow }
	);

	const getImageAsBase64 = (blob): Promise<string> => {
		return new Promise((resolve, rej) => {
			const fileReader = new FileReader();
			fileReader.onerror = () => rej();
			fileReader.onloadend = () => {
				resolve(fileReader.result as string);
			};

			fileReader.readAsDataURL(blob);
		});
	};

	const downloadPdf = async (event: any) => {
		const { data, filename = 'Preventivo_con_Quicko.pdf' } = event;
		quotationRecorder.ensureCode(data);
		let offer: any = {};


		offer.km_totali = totalKm;
		offer.durata = rentalDuration;
		offer.anticipo = advancePayment;
		offer.noleggiatore = data?.pdf?.processedRow?.original?.noleggiatore;

		if (data?.pdf?.dealerDetail?.logo) {
			const logoData = await fetch(data?.pdf?.dealerDetail?.logo);
			const logoDataBlob = await logoData.blob();
			data.pdf.dealerDetail.logoDataUrl = await getImageAsBase64(logoDataBlob);
		}

		const isArvalPrivati = offer.noleggiatore === 'arval privati';
		let canone = data?.pdf?.processedRow?.original?.updated_canone || 0;

		if (data?.pdf?.clientType === 'private') {
			if (isArvalPrivati) {
				offer.canone = canone?.toFixed(2);
			} else {
				offer.canone = (canone * 1.22)?.toFixed(2);
			}
		} else {
			if (isArvalPrivati) {
				offer.canone = (canone - canone * 0.22)?.toFixed(2);
			} else {
				offer.canone = canone?.toFixed(2);
			}
		}

		let totlen = 0;
		data?.optionals?.map((optional) => (totlen += optional.descrizione?.length || 0));

		data.isArvalPrivati = isArvalPrivati;
		data.totlen = totlen;

		setData(data);
		setoffer(offer);
		setFilename(filename);
	};

	useEffect(() => {
		if (!_.isEmpty(data) && !_.isEmpty(offer) && filename) {
			generatePdf(
				(fileName, pdfBlob) => {
					quotationRecorder.record(data, offer, checkedServices?.join('\n'));
					setData({});
					setoffer({});
					setFilename(null);
					if (quickoHelper.canCreateReport) {
						const signalingData = quickoHelper.extractSignalingData(data);
						signalingData.pdfBlob = pdfBlob;
						signalingData.fileName = fileName;
						closeModals?.()
						openSignalingModal(signalingData)
					}
				},
				'#quickoPdf',
				filename,
				{
					...data, source: 'quicko_pdf',
				},
				!quickoHelper.canCreateReport
			);
		}
	}, [data, filename, offer]);

	useEffect(() => {
		return () => unsubscribe();
	})

	const reminderNumber = useMemo(() => resolveReminderNumber(data), [data]);

	return (
		<div className="pdf-container">
			<div id="quickoPdf" className="pdf">
				<table>
					<tbody>
						<tr>
							<td className="col-8">
								<img src="/images/pdf/horizon-c.png" className="logo" />
								<div className="space-block"></div>
								<p className="mb-1 big">
									{t('dear')} {data?.pdf?.name}
								</p>
								<p className="big">
									{t('following_our_rental_offer_for_vehicle')}
								</p>

								<table>
									<tbody>
										<tr>
											<td className="col-6 car-preview">
												{!!data?.image_list?.length && (
													<>
														{data?.image_list[0].base64 ? (
															<img src={data?.image_list[0].base64} />
														) : (
															<img
																src={data?.image_list[0]?.url?.replace(
																	'https://api.horizonautomotive.it',
																	`${process.env.REACT_APP_API_URL}/images`
																)}
															/>
														)}
													</>
												)}
											</td>
											<td className="col-6 p-4">
												<div className="fw-bold">
													{data?.dati_veicolo?.marca}&nbsp;&nbsp;
													{data?.dati_veicolo?.modello.replaceAll(
														' ',
														'\xa0\xa0'
													)}
												</div>
												<div className="fw-bold">
													{(
														data?.row?.versione_locale ||
														data?.dati_veicolo?.allestimento ||
														''
													).replaceAll(' ', '\xa0\xa0')}
												</div>

												<p className="small m-0">
													{t('all_images_for_illustrative_purposes')}
												</p>
											</td>
										</tr>
									</tbody>
								</table>
								<hr className="light clear mx-5" />
								<table className="mt-3">
									<tbody>
										<tr>
											<td className="big-label">{t('rental_fee')}</td>
											<td className="big fw-bold">
												{numberFormat(offer?.canone) + ' € / mese'}
											</td>
										</tr>
										<tr>
											<td className="big-label">{t('duration')}</td>
											<td className="big fw-bold">
												{offer?.durata} {t('months')}
											</td>
										</tr>
										<tr>
											<td className="big-label">{t('km_included')}</td>

											<td className="big fw-bold">
												{data.isArvalPrivati
													? `${numberFormat(100_000)}km *`
													: `${numberFormat(offer?.km_totali)} / anno`}
											</td>
										</tr>
									</tbody>
								</table>
							</td>
							<td className="col-4">
								{data?.pdf?.dealerDetail?.logoDataUrl && (
									<div
										style={{
											display: 'flex',
											alignItems: 'center',
											justifyContent: 'center',
										}}
									>
										<img
											className="mb-3"
											src={data?.pdf?.dealerDetail?.logoDataUrl}
											style={{ objectFit: 'contain', maxWidth: 250, maxHeight: 120 }}
											alt=""
										/>
									</div>
								)}
								<table className="info-user table-border mb-3">
									<tbody>
										<tr>
											<td></td>
										</tr>
										<tr>
											<td className="col-12">
												<div className="p-2 pt-0">
													{t('author')}
													<div className="fw-bold text-spacing">
														{data?.pdf?.userName + ' ' + data?.pdf?.userSurname}
													</div>
												</div>
											</td>
										</tr>
										<tr>
											<td className="col-12">
												<div className="p-2 pt-0">
													{t('mail')}
													<div className="fw-bold text-spacing">
														{data?.pdf?.userMail}
													</div>
												</div>
											</td>
										</tr>
										{data?.pdf?.userPhone ? (
											<tr>
												<td className="col-12">
													<div className="p-2 pt-0">
														{t('phone')}
														<div className="fw-bold text-spacing">
															{data?.pdf?.userPhone}
														</div>
													</div>
												</td>
											</tr>
										) : (
											''
										)}
									</tbody>
								</table>
								<div className="mb-3">
									{t('reminder_number')}
									<div className="fw-bold">{reminderNumber}</div>
								</div>

								<div className="mb-3">
									{t('recipient')}
									<div className="fw-bold">
										{data?.pdf?.name?.replaceAll(' ', '\xa0\xa0')}
									</div>
								</div>

								<div>
									{t('validity_date')}
									<div className="fw-bold">
										{data?.pdf?.data_validita ??
											dayjs().add(3, 'weeks').format('DD/MM/YYYY')}
									</div>
								</div>
							</td>
						</tr>
					</tbody>
				</table>
				{data.isArvalPrivati ? (
					<div className="disclaimer-km">
						* {t('offer_includes_100000_km_for_contract_duration')}
					</div>
				) : (
					''
				)}
				<hr className="big" />

				<table>
					<tbody>
						<tr>
							<td className="col-4 p-2">
								<img
									src={`/images/pdf/chracteristics-${process.env.REACT_APP_MARKET}.png`}
									className="float-start pdf-icon"
								/>
							</td>
							<td className="col-4 p-2">
								<img
									src={`/images/pdf/included_services-${process.env.REACT_APP_MARKET}.png`}
									className="float-start pdf-icon"
								/>
							</td>
							<td className="col-4 p-2">
								<img
									src={`/images/pdf/offer-${process.env.REACT_APP_MARKET}.png`}
									className="float-start pdf-icon"
								/>
							</td>
						</tr>
					</tbody>
				</table>

				<hr className="big" />

				<table className="info">
					<tbody>
						<tr>
							<td className="col-4 col-border">
								<div className="p-2 pt-0  ">
									{(data
										? [
											{
												label: i18next.t('fuel_type'),
												value: data.dati_veicolo?.alimentazione,
											},
											{
												label: i18next.t('kw'),
												value: data.dati_veicolo?.kw
													? `${data.dati_veicolo?.kw} kw`
													: undefined,
											},
											{
												label: i18next.t('cylinder_capacity'),
												value: data.dati_veicolo?.cilindrata,
											},
											{
												label: i18next.t('vehicle_transmission'),
												value: data.dati_veicolo?.cambio,
											},
											{
												label: i18next.t('vehicle_drive_system'),
												value: data.dati_veicolo?.trazione,
											},
											{
												label: i18next.t('doors'),
												value: data.dati_veicolo?.porte,
											},
											{
												label: i18next.t('seats'),
												value: data.dati_veicolo?.posti,
											},
										]
										: []
									).map((spec, index) => {
										return spec.value ? (
											<div key={index}>
												{spec.label}: {spec.value}
											</div>
										) : null;
									})}
								</div>

								<div className="gray-box fw-bold pe-0 w-100">
									{'Accessori e optionals aggiuntivi'.replaceAll(
										' ',
										'\xa0\xa0'
									)}
									&nbsp;&nbsp; ({data?.optionals?.length})
								</div>
								<div className="p-2 pt-0">
									{data.totlen < maxlen ? (
										<ul>
											{data?.optionals?.map((optional, index: number) => (
												<li key={index}>
													{_.upperFirst(optional.descrizione.toLowerCase())}
												</li>
											))}
										</ul>
									) : (
										<span>{t('view_optionals_on_following_page')}</span>
									)}
								</div>
							</td>
							<td className="col-4 col-border">
								<div className="p-2 pt-0">
									<ul>
										{checkedServices?.map((service, index) => (
											<li key={index}>{service}</li>
										))}
										{/* {data?.pdf?.services
                      ?.split(/[\n]+/)
                      .map((service: string, index: number) => {
                        service = service.trim()
                        if (service.length) {
                          return <li key={index}>{service}</li>
                        }
                        return null
                      })} */}
									</ul>
								</div>
							</td>
							<td className="col-4">
								<div className="p-2  pt-0">
									<div className="float-start w-100 mb-2">
										<div className="float-start fw-bold">
											{fixSpaces(t('total_rental_fee') + ' ')} <br />
											<div>
												{fixSpaces(
													data?.pdf?.clientType === 'company'
														? i18next.t('vat_excluded')
														: i18next.t('vat_included') + ' '
												)}
											</div>
										</div>
										<div className="float-end fw-bold">
											{numberFormat(offer?.canone)} € *
										</div>
									</div>
									<hr className="light clear" />
									<div className="mt-2 float-start w-100 fw-bold">
										<div className="float-start big-label">
											{t('downpayment')}
										</div>
										<div className="float-end big mb-1">
											{numberFormat(offer?.anticipo) || 0} € **
										</div>
									</div>
									<hr className="light clear" />*{' '}
									{t('quoted_rate_excludes_additional_accessories')}
									<br />
									<br />
									{`** ${i18next.t('all_downpayments_exempt_from_vat')}`}
								</div>
							</td>
						</tr>
					</tbody>
				</table>

				<table className="mt-1" style={{}}>
					<tbody>
						<tr>
							<td className="footer">
								<img src="/images/pdf/logo-footer.png" className="float-end" />
								<br />
								{t('offer_valid_15_business_days')}
							</td>
						</tr>
					</tbody>
				</table>

				{data.totlen >= maxlen ? (
					<>
						<table className="optionals">
							<tbody>
								<tr>
									<td className="pt-3">
										<img
											src="/images/pdf/horizon-c.png"
											className="logo mb-0"
										/>
									</td>
								</tr>
								<tr>
									<td className="">
										<span className="fw-bold big">
											{'Accessori e optionals aggiuntivi'.replaceAll(
												' ',
												'\xa0\xa0'
											)}
											&nbsp;&nbsp; ({data?.optionals?.length})
										</span>
										<ul>
											{data?.optionals?.map((optional, index: number) => (
												<li key={index}>
													{_.upperFirst(optional.descrizione.toLowerCase())}
												</li>
											))}
										</ul>
									</td>
								</tr>
							</tbody>
						</table>

						<table className="mt-5">
							<tbody>
								<tr>
									<td className="footer">
										<img
											src="/images/pdf/logo-footer.png"
											className="float-end"
										/>
										<br />
										{t('offer_valid_15_business_days')}
									</td>
								</tr>
							</tbody>
						</table>
					</>
				) : (
					''
				)}
			</div>
		</div>
	);
}

export default QuickoPdfTemplate;
